
import { useState } from "react";
import {
  BarChart2,
  ShoppingCart,
  Activity,
  ChevronLeft,
  ChevronRight,
  HomeIcon,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Earth,
  Calculator,
  Lock,
  FlaskConical,
  Shield,
  Repeat,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { RobotFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useLayout } from "../../contexts/LayoutContext";
import { usePermissions } from "../../hooks/usePermissions";

interface MenuItem {
  title: string;
  path: string;
  icon?: React.ReactNode;
  badge?: string;
  isHeader?: boolean;
  isDivider?: boolean;
  isBeta?: boolean;
  children?: MenuItem[];
  isExpanded?: boolean;
}

// Check if running in production
const isProduction =
  process.env.REACT_APP_ENV === "prod" ||
  process.env.ENV === "prod" ||
  window.location.hostname === "ops.adrsta.ai";

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    path: "/",
    icon: <HomeIcon className="w-5 h-5" />,
  },
  {
    title: "Planning",
    path: "/planning/marketing-mix-model-landing",
    icon: <Activity className="w-5 h-5" />,
  },
  {
    title: "Buying",
    path: "/optimization/landing",
    icon: <ShoppingCart className="w-5 h-5" />,
  },
  {
    title: "Causal Loop",
    path: "/causal-loop/landing",
    icon: <Repeat className="w-5 h-5" />,
    children: [
      { title: "With Synthetic Control", path: "/causal-loop/landing" },
      { title: "With RCT", path: "/causal-loop-rct/landing" },
    ],
  },
  {
    title: "Measurement",
    path: "/measurement/geo-lift",
    icon: <BarChart2 className="w-5 h-5" />,
  },
  {
    title: "RCT Experiments",
    path: "/rct-experiment/landing",
    icon: <FlaskConical className="w-5 h-5" />,
  },
  ...(!isProduction
    ? [
        {
          title: "Power Calculator",
          path: "/power-calculator/landing",
          icon: <Calculator className="w-5 h-5" />,
        },
      ]
    : []),
  {
    title: "Performance Agent",
    path: "/agent",
    icon: <RobotFilled className="w-5 h-5" />,
  },
  {
    isDivider: true,
    title: "",
    path: "",
  },
  {
    isHeader: true,
    title: "INTEGRATIONS",
    path: "",
  },
  {
    title: "Media Channels",
    path: "/facebook/integrations",
    icon: <Earth className="w-5 h-5" />,
  },
  {
    title: "CRM Channels",
    path: "/crm/integrations",
    icon: <BarChart2 className="w-5 h-5" />,
  },
  {
    title: "E-commerce Channels",
    path: "/e-commerce/integrations",
    icon: <BarChart2 className="w-5 h-5" />,
  },
  {
    isDivider: true,
    title: "",
    path: "",
  },
  {
    isHeader: true,
    title: "ACCOUNT",
    path: "",
  },
  {
    title: "Security",
    path: "/security",
    icon: <Shield className="w-5 h-5" />,
  },
];

export default function AppSidebar() {
  const { isSidebarExpanded, setSidebarExpanded } = useLayout();
  const { checkMenuPermission } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string, hasPermission: boolean) => {
    if (path && hasPermission) {
      navigate(path);
    }
  };

  const toggleExpand = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const getFirstSegment = (path: string) => {
    const parts = path.split("/").filter(Boolean);
    return parts[0] || "";
  };

  // Map /jobs/:jobType routes back to their parent menu segment so the menu
  // stays highlighted while the user is inside a job creation/view wizard.
  const jobTypeToSegment: Record<string, string> = {
    BidOptimizer: "optimization",
    NextBestAction: "optimization",
    MMM: "planning",
    GeoLift: "measurement",
    CausalLoop: "causal-loop",
    CausalLoopRct: "causal-loop-rct",
  };

  const getActiveSegment = (pathname: string): string => {
    if (pathname.startsWith("/jobs/")) {
      const jobType = pathname.split("/")[2] || "";
      return jobTypeToSegment[jobType] || "";
    }
    return getFirstSegment(pathname);
  };

  const renderMenuItem = (item: MenuItem, index: number, level: number = 0) => {
    if (item.isDivider) {
      return <div key={index} className="my-4 border-t border-gray-200" />;
    }

    if (item.isHeader) {
      return isSidebarExpanded ? (
        <div
          key={index}
          className="px-4 py-2 text-xs font-semibold text-gray-400"
        >
          {item.title}
        </div>
      ) : null;
    }

    const hasPermission = checkMenuPermission(item.path);
    const hasChildren = item.children && item.children.length > 0;
    // A parent with children is "active" whenever the current route matches
    // any of its children's own segments - not just the parent's own path -
    // so e.g. "Causal Loop" stays highlighted while inside either "With
    // Synthetic Control" (/causal-loop/...) or "With RCT" (/causal-loop-rct/...).
    const activeSegment = getActiveSegment(location.pathname);
    const isActive =
      item.path === "/"
        ? location.pathname === "/"
        : hasChildren
        ? item.children!.some((child) => getFirstSegment(child.path) === activeSegment)
        : activeSegment === getFirstSegment(item.path);
    const isExpanded = expandedItems.includes(item.title);

    const menuContent = (
      <div
        className={`flex items-center px-4 py-2 rounded-lg transition-colors relative
          ${
            isActive && hasPermission
              ? "bg-amber-500/10 text-amber-600"
              : hasPermission
              ? "text-gray-600 hover:bg-gray-100 cursor-pointer"
              : "text-gray-400 cursor-not-allowed opacity-60"
          }
          ${item.isBeta ? "opacity-60" : ""}
          ${level > 0 ? "ml-4" : ""}`}
        onClick={() => {
          if (hasChildren && hasPermission) {
            toggleExpand(item.title);
          } else if (hasPermission) {
            handleNavigation(item.path, hasPermission);
          }
        }}
      >
        {item.icon && (
          <span
            className={`${
              isActive && hasPermission
                ? "text-amber-600"
                : hasPermission
                ? "text-gray-500"
                : "text-gray-400"
            }`}
          >
            {item.icon}
          </span>
        )}
        {isSidebarExpanded && <span className="ml-3 flex-1">{item.title}</span>}

        {!hasPermission && (
          <Lock
            className={`w-4 h-4 text-gray-400 ${
              isSidebarExpanded
                ? "ml-auto"
                : "absolute -top-1 -right-1 bg-white rounded-full p-0.5"
            }`}
          />
        )}

        {isSidebarExpanded && item.badge && hasPermission && (
          <span className="ml-auto text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
            {item.badge}
          </span>
        )}
        {isSidebarExpanded && item.isBeta && hasPermission && (
          <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
            Beta
          </span>
        )}
        {isSidebarExpanded && hasChildren && hasPermission && (
          <span className="ml-auto">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
          </span>
        )}
      </div>
    );

    return (
      <div key={index} className="relative">
        {!hasPermission ? (
          <Tooltip
            title="You do not have permission to access this menu"
            placement={isSidebarExpanded ? "right" : "right"}
          >
            {menuContent}
          </Tooltip>
        ) : (
          menuContent
        )}

        {isSidebarExpanded && hasChildren && isExpanded && hasPermission && (
          <div className="mt-1 overflow-y-auto max-h-96">
            {item.children?.map((child, childIndex) =>
              renderMenuItem(child, childIndex, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex">
      <div
        className={`h-screen bg-white transition-all duration-300 border-r relative ${
          isSidebarExpanded ? "w-64" : "w-20"
        }`}
      >
        <button
          onClick={() => setSidebarExpanded(!isSidebarExpanded)}
          className="absolute -right-3 top-4 bg-white border rounded-full p-1 hover:bg-gray-100 z-10"
        >
          {isSidebarExpanded ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        <div className="space-y-1 p-4 h-full overflow-y-auto overflow-x-hidden">
          {menuItems.map((item, index) => renderMenuItem(item, index))}
        </div>
      </div>
    </div>
  );
}
